import type React from "react";
import { useState } from "react";
import classNames from "classnames";
import {
    BreadcrumbItem,
    Breadcrumbs,
} from "@design-system/Breadcrumbs/Breadcrumbs";
import { Avatar } from "@design-system/Avatar/Avatar";
import { Link } from "@design-system/Link/Link";
import { VStack } from "@design-system/VStack/VStack";
import { useAnatomyMode } from "./componentAnatomyMode";
import {
    BooleanControl,
    SelectControl,
    VariantGrid,
    VariantItem,
} from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import controlStyles from "./structureLayoutControls.module.scss";
import styles from "./BreadcrumbsPlayground.module.scss";

const levelOptions = ["1", "2", "3"] as const;

type LevelOption = (typeof levelOptions)[number];

/** An ancestor level; one without `href` renders as static text. */
interface TrailAncestor {
    label: string;
    href?: string;
}

const linkedAncestors: readonly TrailAncestor[] = [
    { label: "Reports", href: "#reports" },
    { label: "Cash flow", href: "#cash-flow" },
];
const staticFirstAncestors: readonly TrailAncestor[] = [
    { label: "Reports" },
    { label: "Cash flow", href: "#cash-flow" },
];
const currentLabel = "Operating activities for payroll and contractors";
const importSteps = ["Upload", "Map columns", "Review"];

const ancestorsForLevel = (
    ancestors: readonly TrailAncestor[],
    level: LevelOption,
) => ancestors.slice(0, Number(level) - 1);

/** A route trail: the given ancestors, then the current page. */
const RouteTrail = ({ ancestors }: { ancestors: readonly TrailAncestor[] }) => (
    <Breadcrumbs>
        {ancestors.map(({ label, href }) =>
            href === undefined ? (
                <BreadcrumbItem key={label}>{label}</BreadcrumbItem>
            ) : (
                <BreadcrumbItem key={label} href={href}>
                    {label}
                </BreadcrumbItem>
            ),
        )}
        <BreadcrumbItem current>{currentLabel}</BreadcrumbItem>
    </Breadcrumbs>
);

/** Wizard trail: past steps go back, the future step is disabled until reached. */
const StepTrail = () => {
    const [currentStepIndex, setCurrentStepIndex] = useState(1);
    const isLastStep = currentStepIndex === importSteps.length - 1;

    return (
        <VStack gap={8} align="start">
            <Breadcrumbs aria-label="Import steps">
                {importSteps.map((step, index) =>
                    index === currentStepIndex ? (
                        <BreadcrumbItem key={step} current>
                            {step}
                        </BreadcrumbItem>
                    ) : (
                        <BreadcrumbItem
                            key={step}
                            onClick={() => setCurrentStepIndex(index)}
                            disabled={index > currentStepIndex}
                        >
                            {step}
                        </BreadcrumbItem>
                    ),
                )}
            </Breadcrumbs>
            <Link
                size="xs"
                disabled={isLastStep}
                onClick={() => setCurrentStepIndex(currentStepIndex + 1)}
            >
                Next step
            </Link>
        </VStack>
    );
};

const TrailFrame = ({
    isNarrow = false,
    children,
}: {
    isNarrow?: boolean;
    children: React.ReactNode;
}) => (
    <div
        className={classNames(styles.trailFrame, {
            [styles.narrowFrame]: isNarrow,
        })}
    >
        {children}
    </div>
);

/**
 * @purpose Breadcrumbs sandbox: levels, a static ancestor and a narrow box that
 * makes the current item truncate (hover it for the full label), plus the
 * Figma levels, a wizard step trail with a disabled future step, and the x-ray.
 */
export const BreadcrumbsPlayground = () => {
    const [level, setLevel] = useState<LevelOption>("3");
    const [hasStaticAncestor, setHasStaticAncestor] = useState(false);
    const [isNarrow, setIsNarrow] = useState(false);

    const anatomy = useAnatomyMode({
        component: "breadcrumbs",
        variantId: level,
        isDisabled: false,
    });

    const summary = `${level} ${level === "1" ? "level" : "levels"}`;

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <section>
                        <h2>Trail</h2>
                        <div className={controlStyles.fields}>
                            <SelectControl
                                label="Levels"
                                options={levelOptions}
                                value={level}
                                onChange={setLevel}
                            />
                        </div>
                    </section>
                    <section>
                        <h2>Features</h2>
                        <div className={controlStyles.booleanGrid}>
                            <BooleanControl
                                label="Static first ancestor"
                                checked={hasStaticAncestor}
                                disabled={level === "1"}
                                onChange={setHasStaticAncestor}
                            />
                            <BooleanControl
                                label="Narrow box"
                                checked={isNarrow}
                                onChange={setIsNarrow}
                            />
                        </div>
                        <p className={controlStyles.hint}>
                            Ancestors with <code>href</code>,{" "}
                            <code>render</code> or <code>onClick</code> are
                            Links; without one they are static text. The{" "}
                            <code>current</code> item is the only one that
                            truncates, and shows its full label in a tooltip
                            while cut off.
                        </p>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={anatomy.isActive ? `x-ray · ${summary}` : summary}
                belowStage={
                    <>
                        <VariantGrid title="Levels (Figma)" columns="full">
                            {levelOptions.map((option) => (
                                <VariantItem key={option} label={option}>
                                    <RouteTrail
                                        ancestors={ancestorsForLevel(
                                            linkedAncestors,
                                            option,
                                        )}
                                    />
                                </VariantItem>
                            ))}
                        </VariantGrid>
                        <VariantGrid title="Item modes" columns="full">
                            <VariantItem label="static ancestor">
                                <RouteTrail
                                    ancestors={ancestorsForLevel(
                                        staticFirstAncestors,
                                        "2",
                                    )}
                                />
                            </VariantItem>
                            <VariantItem label="truncated current">
                                <TrailFrame isNarrow>
                                    <RouteTrail
                                        ancestors={ancestorsForLevel(
                                            linkedAncestors,
                                            "2",
                                        )}
                                    />
                                </TrailFrame>
                            </VariantItem>
                            <VariantItem label="step trail">
                                <StepTrail />
                            </VariantItem>
                            <VariantItem label="icons, truncated current">
                                <TrailFrame isNarrow>
                                    <Breadcrumbs>
                                        <BreadcrumbItem
                                            leftIcon={
                                                <Avatar
                                                    name="Zhong & Sanches"
                                                    size="xs"
                                                    shape="square"
                                                />
                                            }
                                        >
                                            Zhong & Sanches
                                        </BreadcrumbItem>
                                        <BreadcrumbItem
                                            current
                                            leftIcon={
                                                <Avatar
                                                    name="Northwind Holdings"
                                                    size="xs"
                                                    shape="square"
                                                />
                                            }
                                        >
                                            Northwind Holdings International LLC
                                        </BreadcrumbItem>
                                    </Breadcrumbs>
                                </TrailFrame>
                            </VariantItem>
                        </VariantGrid>
                    </>
                }
                output={JSON.stringify(
                    {
                        levels: Number(level),
                        staticFirstAncestor: level !== "1" && hasStaticAncestor,
                        narrowBox: isNarrow,
                    },
                    null,
                    2,
                )}
            >
                {anatomy.view ?? (
                    <TrailFrame isNarrow={isNarrow}>
                        <RouteTrail
                            ancestors={ancestorsForLevel(
                                hasStaticAncestor
                                    ? staticFirstAncestors
                                    : linkedAncestors,
                                level,
                            )}
                        />
                    </TrailFrame>
                )}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
